import { reasonSchema } from '@mfos/shared';
import { type SubmitEvent, type ReactNode, useState } from 'react';
import { useI18n } from '../lib/i18n/runtime';
import { Dialog } from './Dialog';
import { ErrorNotice } from './ErrorNotice';
import { TextAreaField } from './Field';

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly description?: ReactNode;
  readonly confirmLabel: string;
  /** Dismiss label; override when the action itself is a "cancel" so the two buttons differ. */
  readonly cancelLabel?: string;
  readonly tone?: 'danger' | 'default';
  /** Collects a mandatory justification that is stored in the audit trail. */
  readonly requireReason?: boolean;
  readonly reasonLabel?: string;
  readonly isPending: boolean;
  readonly error?: unknown;
  /** Extra fields rendered above the reason (controlled by the caller). */
  readonly children?: ReactNode;
  /** Caller-side validation of extra fields; returning a message blocks submission. */
  readonly validateExtra?: () => string | null;
  readonly onConfirm: (reason: string) => void;
  readonly onCancel: () => void;
}

/** Explicit confirmation step for destructive, governance, equity and security actions. */
export function ConfirmDialog(props: ConfirmDialogProps) {
  return (
    <Dialog
      open={props.open}
      title={props.title}
      onClose={props.isPending ? () => undefined : props.onCancel}
      description={props.description}
    >
      <ConfirmForm {...props} />
    </Dialog>
  );
}

function ConfirmForm({
  confirmLabel,
  cancelLabel,
  tone = 'default',
  requireReason = false,
  reasonLabel,
  isPending,
  error,
  children,
  validateExtra,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>();
  const [extraError, setExtraError] = useState<string | null>(null);

  function submit(event: SubmitEvent) {
    event.preventDefault();
    const extra = validateExtra?.() ?? null;
    setExtraError(extra);
    if (requireReason) {
      const parsed = reasonSchema.safeParse(reason);
      if (!parsed.success) {
        setReasonError(t('reasonMin'));
        return;
      }
      setReasonError(undefined);
    }
    if (extra !== null) {
      return;
    }
    onConfirm(reason.trim());
  }

  return (
    <form onSubmit={submit} noValidate>
      {children}
      {requireReason && (
        <TextAreaField
          label={reasonLabel ?? t('reason')}
          value={reason}
          onValueChange={setReason}
          error={reasonError}
          hint={t('recordedInAudit')}
          required
          maxLength={2000}
        />
      )}
      {extraError !== null && (
        <p className="field__error" role="alert">
          <span aria-hidden="true">⚠ </span>
          {extraError}
        </p>
      )}
      <ErrorNotice error={error} />
      <div className="dialog__actions">
        <button
          type="button"
          className="button button--secondary"
          onClick={onCancel}
          disabled={isPending}
        >
          {cancelLabel ?? t('cancel')}
        </button>
        <button
          type="submit"
          className={`button${tone === 'danger' ? ' button--danger' : ''}`}
          disabled={isPending}
        >
          {isPending ? t('working') : confirmLabel}
        </button>
      </div>
    </form>
  );
}
